import { createSlice } from "@reduxjs/toolkit";
import { startReplaySession, endReplaySession } from "./replaySlice";

const initialState = {
  // === MAIN GAME ANIMATION STATES ===
  counterAnimationStarted: false, // Tracks if the counter animation is started
  counterAnimationFinished: false, // Tracks if the counter animation is finished
  skipCounterAnimation: false, // Tracks if user wants to skip counter animation immediately
  linesRotationFinished: false, // Tracks if the lines rotation is finished
  currentWinningLine: null, // Tracks the current winning line
  winAnimationsPlaying: false, // Tracks if win animations (spine + counter) are playing
  isAnimatedWin: false, // Tracks if current win qualifies for Big Win animation (set immediately on spin result)
  criticalAnimationPlaying: false, // Universal blocker - blocks user input during critical animations (expanding wilds, intro sequences, etc.)
  userWantsToDismissWinAnimation: false, // Tracks if user wants to dismiss win animation
  skipWinAnimation: false, // Tracks if user wants to skip win animation immediately
  isWaitingForUserConfirmation: false, // Tracks if animation is waiting for user to dismiss
  animatedWinAmount: 0, // Current animated win amount value (persists across screen resizes)
  resetMainGameVisualEffects: false, // Trigger to reset main game visual effects and animations
  introTransitionActive: false, // Tracks if intro popup transition is active
  showGameAfterTransition: false, // Tracks if game should be shown after transition completes
  errorRecoveryTransitionActive: false, // Tracks if error recovery transition is active (restores reels after freeze)
  errorRecoveryScreenCovered: false, // Tracks if screen is fully covered during error recovery (safe to show UI)
  showHoldAndWinIntro: false, // Tracks if hold and win intro dialog should be shown
  sceneTransitionActive: false, // Tracks if scene transition overlay is active
  showHoldAndWinOutro: false, // Tracks if hold and win outro dialog should be shown
  showHoldAndWinCollector: false, // Tracks if hold and win collector dialog should be shown
  showMaxWin: false, // Tracks if max win dialog should be shown
  expandingWildsOccurred: false, // Tracks if expanding wilds occurred in the current spin
  holdAndWinOutroCounterFinished: false, // Tracks if H&W outro counter animation has completed
  winAnimationCounterFinished: false, // Tracks if Big/Mega/SuperMega win animation counter has completed

  // === FREE SPIN TRANSITION STATES ===
  freeSpinTransitionActive: false, // Tracks if free spin scene transition animation is active
  freeSpinTransitionMidpoint: false, // Fires when transition reaches midpoint (triggers decoration)
  showFreeSpinIntro: false, // Tracks if free spin intro dialog should be shown
  // The MID-ROUND reload popup — "FREE SPINS / n / LEFT", tap to continue.
  // Same spine and same controller as the intro; only the copy, the number and
  // the moment differ. Shown when a reload lands inside a part-played round,
  // and the round does not resume until the player dismisses it.
  showFreeSpinsReload: false,
  // Gamble round dialog (GDD §9) — the wheel shown after free spins are won,
  // where the player collects the package or gambles it for a better tier.
  showGambleDialog: false,
  freeSpinCounterVisible: false, // Tracks if free spin counter spine animation should be visible
  freeSpinCycleComplete: false, // Tracks if all win lines have completed 1 full cycle in free spins
  freeSpinCoverStarted: false, // True when first symbol gets covered (triggers counter update)
  showFreeSpinOutro: false, // Tracks if free spin outro dialog should be shown
  showAdditionalFreeSpins: false, // Tracks if free spin retrigger dialog should be shown

  // === ROYAL COLLECT TRANSITION STATES ===
  showRoyalCollectIntro: false, // Tracks if royal collect intro dialog should be shown
  royalCollectTransitionActive: false, // Tracks if royal collect scene transition animation is active
  showRoyalCollectOutro: false, // Tracks if royal collect outro dialog should be shown
  royalCollectOutroTransitionActive: false, // Tracks if RC outro scene transition (gradient overlay) is active

  // === POT COLLECTION TRANSITION STATES ===
  showPotCollectionIntro: false, // Tracks if pot collection entrance popup should be shown
  potCollectionTransitionActive: false, // Tracks if pot collection scene transition is active
  showPotCollectionOutro: false, // Tracks if pot collection total win popup should be shown
  potCollectionOutroTransitionActive: false, // Tracks if PC outro scene transition is active
  showPotCollectionGrandJackpot: false, // Tracks if pot collection Grand Jackpot popup should be shown
  // Free-spin MAX WIN — the cap hit during the free-spin feature.
  showFreeSpinMaxWin: false,
  // Free-spin TOTAL WIN — everything won across the feature.
  showFreeSpinTotalWin: false,
  // Free-spin ZERO WIN — the feature ended with nothing.
  showFreeSpinZeroWin: false,
  // Base <-> bonus scene wipe. Plays once and closes itself.
  showTransitionDialog: false,
  showPotCollectionMaxWin: false, // Tracks if pot collection Max Win (10,000x) popup should be shown
  showPotCollectionReload: false, // Tracks if pot collection reload/resume popup should be shown
  potCollectionOutroCounterFinished: false, // Tracks if PC outro counter animation has completed
};

const animationsSlice = createSlice({
  name: "animations",
  initialState,
  reducers: {
    // === MAIN GAME ANIMATION REDUCERS ===
    setCounterAnimationStarted: (state, action) => {
      state.counterAnimationStarted = action.payload;
    },

    setCounterAnimationFinished: (state, action) => {
      state.counterAnimationFinished = action.payload;
    },

    setSkipCounterAnimation: (state, action) => {
      state.skipCounterAnimation = action.payload;
    },

    setCurrentWinningLine: (state, action) => {
      state.currentWinningLine = action.payload;
    },

    setWinAnimationsPlaying: (state, action) => {
      state.winAnimationsPlaying = action.payload;
    },

    setIsAnimatedWin: (state, action) => {
      state.isAnimatedWin = action.payload;
    },

    setCriticalAnimationPlaying: (state, action) => {
      state.criticalAnimationPlaying = action.payload;
    },

    setUserWantsToDismissWinAnimation: (state, action) => {
      state.userWantsToDismissWinAnimation = action.payload;
    },

    setSkipWinAnimation: (state, action) => {
      state.skipWinAnimation = action.payload;
    },

    setIsWaitingForUserConfirmation: (state, action) => {
      state.isWaitingForUserConfirmation = action.payload;
    },

    // Update animated win amount value (for persistence across screen resizes)
    updateAnimatedWinAmount: (state, action) => {
      state.animatedWinAmount = action.payload;
    },

    // Reset main game visual effects and animations (triggered during free spin transition)
    setResetMainGameVisualEffects: (state, action) => {
      state.resetMainGameVisualEffects = action.payload;
    },

    // Control intro transition state
    setIntroTransitionActive: (state, action) => {
      state.introTransitionActive = action.payload;
    },

    // Control whether game should be shown after transition
    setShowGameAfterTransition: (state, action) => {
      state.showGameAfterTransition = action.payload;
    },

    // Control error recovery transition state
    setErrorRecoveryTransitionActive: (state, action) => {
      state.errorRecoveryTransitionActive = action.payload;
    },

    // Control error recovery screen covered state
    setErrorRecoveryScreenCovered: (state, action) => {
      state.errorRecoveryScreenCovered = action.payload;
    },

    // Control hold and win intro dialog visibility
    setShowHoldAndWinIntro: (state, action) => {
      state.showHoldAndWinIntro = action.payload;
    },

    // Control scene transition overlay visibility
    setSceneTransitionActive: (state, action) => {
      state.sceneTransitionActive = action.payload;
    },

    // Control hold and win outro dialog visibility
    setShowHoldAndWinOutro: (state, action) => {
      state.showHoldAndWinOutro = action.payload;
    },

    // Control hold and win collector dialog visibility
    setShowHoldAndWinCollector: (state, action) => {
      state.showHoldAndWinCollector = action.payload;
    },

    // Control max win dialog visibility
    setShowMaxWin: (state, action) => {
      state.showMaxWin = action.payload;
    },

    // Track if expanding wilds occurred in current spin
    setExpandingWildsOccurred: (state, action) => {
      state.expandingWildsOccurred = action.payload;
    },

    // Track if H&W outro counter animation has completed
    setHoldAndWinOutroCounterFinished: (state, action) => {
      state.holdAndWinOutroCounterFinished = action.payload;
    },

    // Track if win celebration (Big/Mega/SuperMega) counter animation has completed
    setWinAnimationCounterFinished: (state, action) => {
      state.winAnimationCounterFinished = action.payload;
    },

    // === FREE SPIN TRANSITION REDUCERS ===
    setFreeSpinTransitionActive: (state, action) => {
      state.freeSpinTransitionActive = action.payload;
    },

    // Signal that transition reached its midpoint (triggers decoration animation)
    setFreeSpinTransitionMidpoint: (state, action) => {
      state.freeSpinTransitionMidpoint = action.payload;
    },

    // Control free spin intro dialog visibility
    setShowFreeSpinIntro: (state, action) => {
      state.showFreeSpinIntro = action.payload;
    },

    setShowFreeSpinsReload: (state, action) => {
      state.showFreeSpinsReload = action.payload;
    },

    // Control gamble round dialog visibility
    setShowGambleDialog: (state, action) => {
      state.showGambleDialog = action.payload;
    },

    // Control free spin counter visibility
    setFreeSpinCounterVisible: (state, action) => {
      state.freeSpinCounterVisible = action.payload;
    },

    // Tracks if all win lines completed 1 full cycle in free spins
    setFreeSpinCycleComplete: (state, action) => {
      state.freeSpinCycleComplete = action.payload;
    },
    setFreeSpinCoverStarted: (state, action) => {
      state.freeSpinCoverStarted = action.payload;
    },
    // Control free spin outro dialog visibility
    setShowFreeSpinOutro: (state, action) => {
      state.showFreeSpinOutro = action.payload;
    },
    // Control free spin retrigger dialog visibility
    setShowAdditionalFreeSpins: (state, action) => {
      state.showAdditionalFreeSpins = action.payload;
    },

    // === ROYAL COLLECT TRANSITION REDUCERS ===
    setShowRoyalCollectIntro: (state, action) => {
      state.showRoyalCollectIntro = action.payload;
    },
    setRoyalCollectTransitionActive: (state, action) => {
      state.royalCollectTransitionActive = action.payload;
    },
    setShowRoyalCollectOutro: (state, action) => {
      state.showRoyalCollectOutro = action.payload;
    },
    setRoyalCollectOutroTransitionActive: (state, action) => {
      state.royalCollectOutroTransitionActive = action.payload;
    },

    // === POT COLLECTION TRANSITION REDUCERS ===
    setShowPotCollectionIntro: (state, action) => {
      state.showPotCollectionIntro = action.payload;
    },
    setPotCollectionTransitionActive: (state, action) => {
      state.potCollectionTransitionActive = action.payload;
    },
    setShowPotCollectionOutro: (state, action) => {
      state.showPotCollectionOutro = action.payload;
    },
    setPotCollectionOutroTransitionActive: (state, action) => {
      state.potCollectionOutroTransitionActive = action.payload;
    },
    setShowPotCollectionGrandJackpot: (state, action) => {
      state.showPotCollectionGrandJackpot = action.payload;
    },
    setShowFreeSpinMaxWin: (state, action) => {
      state.showFreeSpinMaxWin = action.payload;
    },
    setShowFreeSpinTotalWin: (state, action) => {
      state.showFreeSpinTotalWin = action.payload;
    },
    setShowFreeSpinZeroWin: (state, action) => {
      state.showFreeSpinZeroWin = action.payload;
    },
    setShowTransitionDialog: (state, action) => {
      state.showTransitionDialog = action.payload;
    },
    setShowPotCollectionMaxWin: (state, action) => {
      state.showPotCollectionMaxWin = action.payload;
    },
    setShowPotCollectionReload: (state, action) => {
      state.showPotCollectionReload = action.payload;
    },
    setPotCollectionOutroCounterFinished: (state, action) => {
      state.potCollectionOutroCounterFinished = action.payload;
    },
  },
  extraReducers: (builder) => {
    // ── Replay session exit: the counter flags belong to the REPLAYED round ──
    //
    // These say "a win has just finished counting", and WinDisplay reads them to
    // decide whether to show a win at all. They are normally cleared by the next
    // spin — but leaving a replay is not a spin, so the last replayed round left
    // them raised over a live game that had not won anything.
    //
    // Invisible in ordinary play, because with no win and no free rounds
    // WinDisplay returns null and nothing is drawn either way. During FREE
    // ROUNDS the component is always mounted for the counter row, so the stale
    // flags finally had something to show themselves on — which is exactly why
    // this only ever reproduced with free rounds active.
    //
    // gameSettingsSlice, freeRoundsSlice and replaySlice all already restore
    // themselves on this action; this slice was simply never added.
    builder.addCase(endReplaySession, (state) => {
      state.counterAnimationStarted = false;
      state.counterAnimationFinished = false;
      state.winAnimationCounterFinished = false;
      state.winAnimationsPlaying = false;
      state.animatedWinAmount = 0;
    });

    // ── The SAME clear on the way IN, minus one flag ──
    //
    // Entering a replay is not a spin either, and the fault is symmetrical: the
    // LIVE round's flags were still raised, so the replay opened with the live
    // win still drawn on it. With free rounds that reads as "Total win 0.42,
    // Win 16.21" — the total correctly rewound to the state before the recorded
    // round, and beside it the win from a round the replay has not played yet.
    //
    // ── `winAnimationsPlaying` is NOT cleared here, and that is the point ──
    //
    // The four below are the INFOLINE's: the counter, its finished markers and
    // the amount it draws. `winAnimationsPlaying` is the BOARD's — the win
    // boxes, the dim and the symbol animations. Clearing it on the way in
    // stopped the win table mid-presentation and left the board dimmed with
    // nothing on it, which reads as the game breaking rather than as a replay
    // starting. So: hide the numbers, leave the board alone. The replay paints
    // its own board over the top a moment later.
    //
    // The EXIT case above still clears all five — there the presentation is
    // genuinely over and the board is being handed back.
    //
    // Safe to run twice: "Replay Again" dispatches startReplaySession a second
    // time, and a clear is idempotent. That is also why this needs no
    // `preReplaySnapshot` guard, unlike the slices that park real state.
    builder.addCase(startReplaySession, (state) => {
      state.counterAnimationStarted = false;
      state.counterAnimationFinished = false;
      state.winAnimationCounterFinished = false;
      state.animatedWinAmount = 0;
    });
  },
});

export const selectIntroTransitionActive = (state) =>
  state.animations.introTransitionActive;

export const selectShowGameAfterTransition = (state) =>
  state.animations.showGameAfterTransition;

export const selectResetMainGameVisualEffects = (state) =>
  state.animations.resetMainGameVisualEffects;

export const selectCounterAnimationFinished = (state) =>
  state.animations.counterAnimationFinished;

export const selectLinesRotationFinished = (state) =>
  state.animations.linesRotationFinished;

export const selectWinAnimationsPlaying = (state) =>
  state.animations.winAnimationsPlaying;

export const selectIsAnimatedWin = (state) => state.animations.isAnimatedWin;

export const selectCriticalAnimationPlaying = (state) =>
  state.animations.criticalAnimationPlaying;

export const selectUserWantsToDismissWinAnimation = (state) =>
  state.animations.userWantsToDismissWinAnimation;

export const selectSkipWinAnimation = (state) =>
  state.animations.skipWinAnimation;

export const selectIsWaitingForUserConfirmation = (state) =>
  state.animations.isWaitingForUserConfirmation;

export const selectAnimatedWinAmount = (state) =>
  state.animations.animatedWinAmount;

export const selectCounterAnimationStarted = (state) =>
  state.animations.counterAnimationStarted;

export const selectSkipCounterAnimation = (state) =>
  state.animations.skipCounterAnimation;

export const selectShowHoldAndWinIntro = (state) =>
  state.animations.showHoldAndWinIntro;

export const selectSceneTransitionActive = (state) =>
  state.animations.sceneTransitionActive;

export const selectShowHoldAndWinOutro = (state) =>
  state.animations.showHoldAndWinOutro;

export const selectShowHoldAndWinCollector = (state) =>
  state.animations.showHoldAndWinCollector;

export const selectShowMaxWin = (state) => state.animations.showMaxWin;

export const selectExpandingWildsOccurred = (state) =>
  state.animations.expandingWildsOccurred;

export const selectHoldAndWinOutroCounterFinished = (state) =>
  state.animations.holdAndWinOutroCounterFinished;

export const selectWinAnimationCounterFinished = (state) =>
  state.animations.winAnimationCounterFinished;

export const selectErrorRecoveryTransitionActive = (state) =>
  state.animations.errorRecoveryTransitionActive;

export const selectErrorRecoveryScreenCovered = (state) =>
  state.animations.errorRecoveryScreenCovered;

export const selectFreeSpinTransitionActive = (state) =>
  state.animations.freeSpinTransitionActive;

export const selectFreeSpinTransitionMidpoint = (state) =>
  state.animations.freeSpinTransitionMidpoint;

export const selectShowGambleDialog = (state) =>
  state.animations.showGambleDialog;
export const selectShowFreeSpinIntro = (state) =>
  state.animations.showFreeSpinIntro;
export const selectShowFreeSpinsReload = (state) =>
  state.animations.showFreeSpinsReload;

export const selectFreeSpinCounterVisible = (state) =>
  state.animations.freeSpinCounterVisible;

export const selectFreeSpinCycleComplete = (state) =>
  state.animations.freeSpinCycleComplete;

export const {
  setCounterAnimationStarted,
  setCounterAnimationFinished,
  setSkipCounterAnimation,
  setCurrentWinningLine,
  setWinAnimationsPlaying,
  setIsAnimatedWin,
  setCriticalAnimationPlaying,
  setUserWantsToDismissWinAnimation,
  setSkipWinAnimation,
  setIsWaitingForUserConfirmation,
  updateAnimatedWinAmount,
  setResetMainGameVisualEffects,
  setIntroTransitionActive,
  setShowGameAfterTransition,
  setErrorRecoveryTransitionActive,
  setErrorRecoveryScreenCovered,
  setShowHoldAndWinIntro,
  setSceneTransitionActive,
  setShowHoldAndWinOutro,
  setShowHoldAndWinCollector,
  setShowMaxWin,
  setExpandingWildsOccurred,
  setHoldAndWinOutroCounterFinished,
  setWinAnimationCounterFinished,
  setFreeSpinTransitionActive,
  setFreeSpinTransitionMidpoint,
  setShowFreeSpinIntro,
  setShowFreeSpinsReload,
  setShowGambleDialog,
  setFreeSpinCounterVisible,
  setFreeSpinCycleComplete,
  setFreeSpinCoverStarted,
  setShowFreeSpinOutro,
  setShowAdditionalFreeSpins,
  setShowRoyalCollectIntro,
  setRoyalCollectTransitionActive,
  setShowRoyalCollectOutro,
  setRoyalCollectOutroTransitionActive,
  setShowPotCollectionIntro,
  setPotCollectionTransitionActive,
  setShowPotCollectionOutro,
  setPotCollectionOutroTransitionActive,
  setShowPotCollectionGrandJackpot,
  setShowFreeSpinMaxWin,
  setShowFreeSpinTotalWin,
  setShowFreeSpinZeroWin,
  setShowTransitionDialog,
  setShowPotCollectionMaxWin,
  setShowPotCollectionReload,
  setPotCollectionOutroCounterFinished,
} = animationsSlice.actions;

export const selectFreeSpinCoverStarted = (state) =>
  state.animations.freeSpinCoverStarted;

export const selectShowFreeSpinOutro = (state) =>
  state.animations.showFreeSpinOutro;

export const selectShowAdditionalFreeSpins = (state) =>
  state.animations.showAdditionalFreeSpins;

// Royal Collect selectors
export const selectShowRoyalCollectIntro = (state) =>
  state.animations.showRoyalCollectIntro;

export const selectRoyalCollectTransitionActive = (state) =>
  state.animations.royalCollectTransitionActive;

export const selectShowRoyalCollectOutro = (state) =>
  state.animations.showRoyalCollectOutro;

export const selectRoyalCollectOutroTransitionActive = (state) =>
  state.animations.royalCollectOutroTransitionActive;

// Pot Collection selectors
export const selectShowPotCollectionIntro = (state) =>
  state.animations.showPotCollectionIntro;

export const selectPotCollectionTransitionActive = (state) =>
  state.animations.potCollectionTransitionActive;

export const selectShowPotCollectionOutro = (state) =>
  state.animations.showPotCollectionOutro;

export const selectPotCollectionOutroTransitionActive = (state) =>
  state.animations.potCollectionOutroTransitionActive;

export const selectShowPotCollectionGrandJackpot = (state) =>
  state.animations.showPotCollectionGrandJackpot;

export const selectShowFreeSpinMaxWin = (state) =>
  state.animations.showFreeSpinMaxWin;

export const selectShowFreeSpinTotalWin = (state) =>
  state.animations.showFreeSpinTotalWin;

export const selectShowFreeSpinZeroWin = (state) =>
  state.animations.showFreeSpinZeroWin;

export const selectShowTransitionDialog = (state) =>
  state.animations.showTransitionDialog;

export const selectShowPotCollectionMaxWin = (state) =>
  state.animations.showPotCollectionMaxWin;

export const selectPotCollectionOutroCounterFinished = (state) =>
  state.animations.potCollectionOutroCounterFinished;

export const selectShowPotCollectionReload = (state) =>
  state.animations.showPotCollectionReload;

export default animationsSlice.reducer;
